import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AdminAuthProvider, useAdminAuth } from "@/context/AdminAuthContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { TenantProvider } from "@/context/TenantContext";
import { PortalShell } from "@/components/layout/PortalShell";
import AdminShell from "@/components/admin/AdminShell";
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import ConsultationsPage from "@/pages/ConsultationsPage";
import ComplimentaryServicesPage from "@/pages/ComplimentaryServicesPage";
import FamilyPage from "@/pages/FamilyPage";
import HealthChecksPage from "@/pages/HealthChecksPage";
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import HrAnalyticsPage from "@/pages/HrAnalyticsPage";
import ProfilePage from "@/pages/ProfilePage";
import LabCheckoutPage from "@/pages/LabCheckoutPage";
import LabTestsPage from "@/pages/LabTestsPage";
import OffersPage from "@/pages/OffersPage";
import ReportsPage from "@/pages/ReportsPage";
import UpdatesPage from "@/pages/UpdatesPage";


function ProtectedRoute() {
  const { isChecking, token } = useAuth();
  if (isChecking) {
    return <div className="app-loading" data-testid="auth-loading-state">Preparing your Health Hub…</div>;
  }
  return token ? <PortalShell /> : <Navigate to="/login" replace />;
}

function AdminProtectedRoute() {
  const { isChecking, token } = useAdminAuth();
  if (isChecking) {
    return <div className="app-loading" data-testid="admin-auth-loading-state">Loading ops dashboard…</div>;
  }
  return token ? <AdminShell /> : <Navigate to="/admin/login" replace />;
}

// The admin branch is deliberately its own island — no TenantProvider/AuthProvider,
// since an Ivory ops account isn't tied to any one client's branding or employee login.
function AdminApp() {
  return (
    <AdminAuthProvider>
      <Routes>
        <Route path="login" element={<AdminLoginPage />} />
        <Route path="*" element={<AdminProtectedRoute />} />
      </Routes>
    </AdminAuthProvider>
  );
}

function EmployeeApp() {
  return (
    <TenantProvider>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<ProtectedRoute />}>
            <Route index element={<HomePage />} />
            <Route path="health-checks" element={<HealthChecksPage />} />
            <Route path="free-services" element={<ComplimentaryServicesPage />} />
            <Route path="family" element={<FamilyPage />} />
            <Route path="consultations" element={<ConsultationsPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="offers" element={<OffersPage />} />
            <Route path="lab-tests" element={<LabTestsPage />} />
            <Route path="lab-tests/checkout" element={<LabCheckoutPage />} />
            <Route path="updates" element={<UpdatesPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="insights" element={<HrAnalyticsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </TenantProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin/*" element={<AdminApp />} />
        <Route path="/*" element={<EmployeeApp />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
